import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip, LayersControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { API } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  BarChart3, Phone, ShieldAlert, FileText, Loader2, MapPin,
} from 'lucide-react';
import SiteFooter from '../components/SiteFooter';
import ModonLogo from '../components/ModonLogo';
import NavCluster from '../components/NavCluster';

const RISK_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#10b981' };
const RISK_LABELS = { high: 'عالي', medium: 'متوسط', low: 'منخفض' };

// Default center: Saudi Arabia (Riyadh)
const DEFAULT_CENTER = [23.8859, 45.0792];
const DEFAULT_ZOOM = 6;

export default function MapPage() {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/map-factories`);
        setPoints(r.data || []);
      } catch (e) {
        // Non-blocking: keep the map page rendered even if the factories
        // endpoint fails. Surface the error for debugging.
        console.warn('[MapPage] failed to load map points', e);
      }
      finally { setLoading(false); }
    })();
  }, []);

  const counts = points.reduce((acc, p) => {
    acc[p.riskLevel] = (acc[p.riskLevel] || 0) + 1;
    return acc;
  }, { high: 0, medium: 0, low: 0 });

  return (
    <div className="modon-app-shell flex flex-col" dir="rtl">
      <header className="modon-header">
        <div className="modon-header-inner max-w-7xl">
          <div className="flex items-center gap-3">
            <ModonLogo size={44}/>
            <div>
              <h1 className="font-extrabold text-slate-900">خريطة المصانع</h1>
              <p className="text-xs text-slate-500">عرض المصانع المسجّلة في النظام</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NavCluster homePath="/admin" testidPrefix="map" />
            <Link to="/dashboard"><Button size="sm" variant="outline"><BarChart3 size={14} className="ml-1"/> الإحصائيات</Button></Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-6 space-y-4">
        {/* Legend */}
        <Card className="p-3 flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-1"><MapPin size={14}/> إجمالي المصانع على الخريطة: <b data-testid="map-total">{points.length}</b></div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: RISK_COLORS.high }}/> عالي: {counts.high || 0}</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: RISK_COLORS.medium }}/> متوسط: {counts.medium || 0}</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: RISK_COLORS.low }}/> منخفض: {counts.low || 0}</span>
          </div>
        </Card>

        {/* Map */}
        <Card className="overflow-hidden" data-testid="leaflet-map-card">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-600 p-6"><Loader2 className="animate-spin" size={16}/> جارٍ التحميل...</div>
          ) : (
            <div style={{ height: '70vh', width: '100%' }}>
              <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
                <LayersControl position="topright">
                  <LayersControl.BaseLayer checked name="قمر صناعي">
                    <TileLayer
                      attribution='Tiles © Esri'
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                      maxZoom={19}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="شوارع (OSM)">
                    <TileLayer
                      attribution='© OpenStreetMap'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                  </LayersControl.BaseLayer>
                </LayersControl>
                {points.map((p) => (
                  <CircleMarker
                    key={p.id}
                    center={[p.lat, p.lng]}
                    radius={10}
                    pathOptions={{ color: RISK_COLORS[p.riskLevel] || '#64748b', fillColor: RISK_COLORS[p.riskLevel] || '#64748b', fillOpacity: 0.7, weight: 2 }}
                  >
                    <Tooltip direction="top" offset={[0, -8]} opacity={1}>
                      <div className="text-xs"><b>{p.factoryName}</b> — {p.riskLabel}</div>
                    </Tooltip>
                    <Popup minWidth={260}>
                      <div dir="rtl" style={{ direction: 'rtl', textAlign: 'right', fontFamily: 'inherit' }}>
                        <div className="font-bold text-slate-900 mb-1">{p.factoryName}</div>
                        <div className="text-xs text-slate-600 space-y-0.5">
                          <div>النشاط: {p.activity || '—'}</div>
                          <div>المدينة: {p.city || '—'}</div>
                          {p.address && <div>العنوان: {p.address}</div>}
                          <div className="flex items-center gap-1 mt-1">
                            <Badge style={{ backgroundColor: RISK_COLORS[p.riskLevel], color: 'white' }}>
                              <ShieldAlert size={10} className="ml-1"/> {p.riskLabel} ({p.riskScore})
                            </Badge>
                          </div>
                          <div className="mt-1"><FileText size={11} className="inline ml-1"/> {p.sdsCount} تقرير • {p.chemicalsCount} مادة</div>
                          {p.phone && <div className="flex items-center gap-1 mt-1"><Phone size={11}/> {p.phone}</div>}
                        </div>
                        <div className="mt-2 flex gap-1 flex-wrap">
                          {(p.sdsIds || []).slice(0, 3).map((sid, i) => (
                            <Link key={sid} to={`/sds/view/${sid}`}>
                              <Button size="sm" variant="outline" className="text-[10px] h-6">SDS {i + 1}</Button>
                            </Link>
                          ))}
                        </div>
                        <div className="mt-2 text-[10px] text-slate-500 border-t pt-1">
                          أرقام الطوارئ: إطفاء 998 • إسعاف 997
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
          )}
        </Card>

        {points.length === 0 && !loading && (
          <Card className="p-6 text-center text-slate-500">
            لا توجد مصانع مسجلة بإحداثيات موقع. سيتم عرضها هنا فور التسجيل مع تحديد الموقع الجغرافي.
          </Card>
        )}
      </main>
      <SiteFooter/>
    </div>
  );
}

